import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

type TFunction = ReturnType<typeof useTranslation>["t"];

/** Error key for the "select at least one alert type" rule — a section-level
 * message with no input of its own. */
export const ALERT_TYPES_ERROR_PATH = "alert_types";

/** How many phone numbers, and separately how many email addresses, one contact
 * may hold — the backend refuses an eleventh (`ContactChannel::MAX_PER_CONTACT`). */
export const MAX_DESTINATIONS_PER_KIND = 10;

const isValidEmail = (value: string) => z.string().email().safeParse(value).success;

/**
 * Schema for the Add / Edit contact drawer. The drawer models a contact the
 * way the design does: a list of phone numbers and a list of email addresses,
 * each carrying the alert types it should serve. `contactId` is set when an
 * existing contact is being edited, empty when the drawer is creating the
 * person.
 */
export const buildOccupantFormSchema = (t: TFunction) =>
  z
    .object({
      contactId: z.string(),
      first_name: z.string().trim(),
      last_name: z.string().trim(),
      phones: z
        .array(
          z.object({
            /** What this number is — "Home", "Work". Free text, optional. */
            label: z.string().trim(),
            value: z.string().trim(),
            alert_call: z.boolean(),
            alert_sms: z.boolean(),
          }),
        )
        .max(
          MAX_DESTINATIONS_PER_KIND,
          t("occupants:occupants-max-phones", "Up to {{max}} numbers", { max: MAX_DESTINATIONS_PER_KIND }),
        ),
      emails: z
        .array(
          z.object({
            label: z.string().trim(),
            value: z.string().trim(),
            alert_email: z.boolean(),
          }),
        )
        .max(
          MAX_DESTINATIONS_PER_KIND,
          t("occupants:occupants-max-emails", "Up to {{max}} emails", { max: MAX_DESTINATIONS_PER_KIND }),
        ),
    })
    .superRefine((values, ctx) => {
      const filledPhones = values.phones.filter((phone) => phone.value !== "");
      const filledEmails = values.emails.filter((email) => email.value !== "");

      // An empty row is an error, however few there are — a box left blank was
      // meant to be filled, and the message belongs on that box. The single
      // exception is a lone blank row in one list while the other already
      // reaches the contact: an email-only occupant shouldn't be nagged for a
      // phone number it was never going to have.
      const isSparePlaceholder = (rowCount: number, otherFilled: number) => rowCount === 1 && otherFilled > 0;

      values.phones.forEach((phone, index) => {
        if (phone.value === "" && !isSparePlaceholder(values.phones.length, filledEmails.length)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["phones", index, "value"],
            message: t("occupants:occupants-validation-phone", "Phone number is required"),
          });
        }

        const digits = phone.value.replace(/\D/g, "");

        // Stored digits-only: a value with none ("none", "n/a") is refused by
        // the contact endpoint rather than silently dropped with the rest.
        if (phone.value !== "" && digits === "") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["phones", index, "value"],
            message: t("occupants:occupants-validation-phone-digits", "Enter a valid phone number"),
          });
        } else if (digits !== "" && (phone.alert_sms || phone.alert_call) && digits.length !== 10) {
          // Texts and calls go out to a ten-digit number only, so the API
          // refuses to subscribe "+1 514…" or a seven-digit number to them.
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["phones", index, "value"],
            message: t("occupants:occupants-validation-phone-ten-digits", "Texts and calls need a 10-digit phone number"),
          });
        }
      });

      values.emails.forEach((email, index) => {
        if (email.value === "") {
          if (!isSparePlaceholder(values.emails.length, filledPhones.length)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["emails", index, "value"],
              message: t("occupants:occupants-validation-email-required", "Email is required"),
            });
          }

          return;
        }

        if (!isValidEmail(email.value)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["emails", index, "value"],
            message: t("occupants:occupants-validation-email", "Enter a valid email address"),
          });
        }
      });

      // An existing contact is edited in place; a new one still needs a name.
      if (values.contactId === "" && values.first_name === "" && values.last_name === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["first_name"],
          message: t("occupants:occupants-validation-first-name", "First name is required"),
        });
      }

      // Every blank row has just been flagged on its own input; repeating it on
      // the form would only say the same thing twice.
      if (filledPhones.length === 0 && filledEmails.length === 0) {
        return;
      }

      const hasAlert =
        filledPhones.some((phone) => phone.alert_call || phone.alert_sms) ||
        filledEmails.some((email) => email.alert_email);

      // Belongs to no single input — reported on the form and rendered under
      // the destination lists. Deliberately NOT `root`: react-hook-form clears
      // `errors.root` inside handleSubmit before deciding whether the form is
      // valid, so a lone root issue submits an empty value object.
      if (!hasAlert) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [ALERT_TYPES_ERROR_PATH],
          message: t("occupants:occupants-validation-alert-type", "Select at least one alert type"),
        });
      }
    });

export const useOccupantFormSchema = () => {
  const { t } = useTranslation(["occupants"]);

  return useMemo(() => buildOccupantFormSchema(t), [t]);
};

export type OccupantFormValues = z.infer<ReturnType<typeof buildOccupantFormSchema>>;

export type OccupantPhoneRow = OccupantFormValues["phones"][number];
export type OccupantEmailRow = OccupantFormValues["emails"][number];

/** A blank phone row — what "Add a number" appends. */
export const EMPTY_PHONE_ROW: OccupantPhoneRow = { label: "", value: "", alert_call: false, alert_sms: false };

/** A blank email row — what "Add an email" appends. */
export const EMPTY_EMAIL_ROW: OccupantEmailRow = { label: "", value: "", alert_email: false };

/** One empty row of each, so the form opens with something to type into.
 * There is no "primary" flag: the portal may not set it, the backend decides. */
export const OCCUPANT_FORM_DEFAULTS: OccupantFormValues = {
  contactId: "",
  first_name: "",
  last_name: "",
  phones: [EMPTY_PHONE_ROW],
  emails: [EMPTY_EMAIL_ROW],
};
